import { Security } from '@blk/awt-aladdinportfoliobuilderserver';
import type {
  ActionEntityRequest as _OriginalActionEntityRequest,
  AddWorkflowItemLogRequest as _OriginalAddWorkflowItemLogRequest,
  WorkflowItemHistoryRequest as _OriginalWorkflowItemHistoryRequest,
  ActionAnalysis,
  ActionEntity,
  ActionItemHistoryInterval,
  ActionItemsRequest,
  ActionTypeTag,
  AddWorkflowItemLogRequest,
  DoubleGroupByRequest,
  SimpleActionItem,
  ValidDate,
  WorkflowItemsSolrResponse,
  WorkflowItemWithLog,
} from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import { AGraphWrappedType, unwrapAGraphResponse } from '../../utils/AGraphWrapper';
import { normalizeNullValuesForAGraph } from '../../utils/ApiUtils';
import { isAGraphEnabled } from './AGraphUtils';
import { awApiWithPkg, awApiWithPkgAndExtendedTimeout } from './Base';

// workflowItemAttributes is not mandatory for this request
type ActionEntityRequest = Omit<_OriginalActionEntityRequest, 'workflowItemAttributes'> &
  Partial<Pick<_OriginalActionEntityRequest, 'workflowItemAttributes'>>;

export function postItemsSolr(
  request: ActionAnalysis<ActionItemsRequest>,
  cancelToken?: CancelToken,
  viewAs?: string
): Promise<AxiosResponse<WorkflowItemsSolrResponse>> {
  const { sortBys } = request.payload;
  // Handle server side derived columns sorting with a valid ADL columns
  const newSortBys = sortBys.map(({ colTag: currColTag, sortDirection: currSortDirection }) => {
    let colTag: string;
    if (currColTag === 'daysAged') {
      // A sorting on daysAged needs to sort on origWorkflowDate instead since daysAged is an internal concept
      colTag = 'origWorkflowDate';
    } else if (currColTag === 'daysToDeadline') {
      // A sorting on deadline needs to sort on config.deadline instead since daysAged is an internal concept
      colTag = 'config.deadline';
    } else if (currColTag === 'daysFromLastSnooze') {
      // A sorting on daysFromLastSnooze needs to sort on lastSnoozeDate instead since daysFromLastSnooze is an internal concept
      colTag = 'lastSnoozeDate';
    } else {
      colTag = currColTag;
    }
    return {
      colTag,
      sortDirection: currSortDirection,
    };
  });
  const newRequest: ActionAnalysis<ActionItemsRequest> = {
    ...request,
    payload: {
      ...request.payload,
      sortBys: newSortBys,
    },
  };
  const finalRequest = isAGraphEnabled() ? normalizeNullValuesForAGraph(newRequest, 'filters') : newRequest;
  const path = '/workflow/items-solr';
  // TODO change this type once we get workflow server onboarded with typescript publishing
  return awApiWithPkg.post<WorkflowItemsSolrResponse>(path, finalRequest, {
    cancelToken,
    params: { isLegacy: false, viewAs, pkg: request.pkg },
  });
}

export async function postSubgroupBySolr(
  request: GetSubgroupRequest,
  cancelToken?: CancelToken,
  viewAs?: string
): Promise<AxiosResponse<GroupedStats[]>> {
  const path = '/workflow/subgroup-by-solr';
  const response = await awApiWithPkg.post<AGraphWrappedType<GroupedStats[], 'items'>>(path, request, {
    cancelToken,
    params: { isLegacy: false, viewAs, pkg: request.pkg },
  });
  return unwrapAGraphResponse(response, 'items');
}

export interface BulkAddWorkflowItemLogRequest {
  items: AddWorkflowItemLogRequest[];
}

export interface Position {
  identifier: string;
  identifierType: string;
  positionID: string;
  security?: Security;
  weight: number;
}

export interface Benchmark {
  benchType: string;
  currency: string;
  name: string;
  positions: Position[];
}

export interface Portfolio {
  accountBaseMarketValue: number;
  analysisDate: string;
  benchmarks: Benchmark[];
  currency: string;
  metadata: any;
  name: string;
  organizationalBaseMarketValue: number;
  positions: Position[];
}

export type GroupedStats = Record<string, string | number>;

export type Source = SimpleActionItem;

export type FailedDate = ActionItemHistoryInterval;

export type WorkflowHistory = SimpleActionItem;

/**
 * WorkflowItemHistoryRequest with purpose made optional.
 * The BE type incorrectly marks `purpose` as required when it should be @Nullable.
 */
export type WorkflowItemHistoryRequest = Omit<_OriginalWorkflowItemHistoryRequest, 'purpose'> & {
  purpose?: string;
};

export type GetSubgroupRequest = ActionAnalysis<DoubleGroupByRequest>;

export function getWorkflowSources(
  payload: WorkflowItemHistoryRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Source[]>> {
  const path = '/workflow/item-sources';
  return awApiWithPkgAndExtendedTimeout.post(path, payload, { cancelToken });
}

/**
 * Request payload for getWorkflowItem.
 * FIXME: WorkflowItemRequest is not exported from @blk/awt-aladdinwealthserver
 */
export interface WorkflowItemRequest {
  actionType?: ActionTypeTag;
  campaignId?: string;
  client: string;
  pkg: string;
  ruleName: string;
  source: string;
  workflowDate: string;
  workflowItemType: string;
  workflowItemValue: string;
}

export function getWorkflowItem(
  payload: WorkflowItemRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<WorkflowItemWithLog>> {
  const path = '/workflow/item';
  return awApiWithPkgAndExtendedTimeout.post(
    path,
    { today: new Date().toISOString().substring(0, 10), ...payload },
    { cancelToken, params: { pkg: payload.pkg } }
  );
}

export function getWorkflowEntities(
  payload: ActionEntityRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<ActionEntity[]>> {
  const path = '/workflow/entities';
  return awApiWithPkg.post(path, payload, { cancelToken, params: { pkg: payload.pkg } });
}

export function postComment(request: AddWorkflowItemLogRequest): Promise<AxiosResponse> {
  const path = '/workflow/comment';
  return awApiWithPkg.post<AxiosResponse>(path, request);
}

export function postComments(request: BulkAddWorkflowItemLogRequest, pkg: string): Promise<AxiosResponse> {
  const path = '/workflow/comments';
  return awApiWithPkg.post<AxiosResponse>(path, request.items, { params: { pkg } });
}

export function getHoldings(
  pkg: string,
  workflowDate: string,
  workflowItemType: string,
  workflowItemValue: string
): Promise<AxiosResponse<Portfolio[]>> {
  const column = workflowItemType === 'ACCOUNT' ? 'aggIDValue' : `metadata.account.${workflowItemType}`;
  const path = `/holdings/${pkg}/${workflowDate}/accounts/with-lots/${column}/${workflowItemValue}`;
  return awApiWithPkg.get(path, { params: { pkg } });
}

export function postHistoryInterval(request: WorkflowItemHistoryRequest): Promise<AxiosResponse<FailedDate[]>> {
  const path = '/workflow/item-history-interval';
  return awApiWithPkg.post(path, request);
}

export function postHistory(request: WorkflowItemHistoryRequest): Promise<AxiosResponse<WorkflowHistory[]>> {
  const path = '/workflow/item-history';
  return awApiWithPkg.post(path, request, { params: { pkg: request.pkg } });
}

export async function getWorkflowDatesApi(): Promise<AxiosResponse<ValidDate[]>> {
  const path = '/dates/workflow';
  const response = await awApiWithPkg.get<AGraphWrappedType<ValidDate[], 'validDates'>>(path);
  return unwrapAGraphResponse(response, 'validDates');
}

// Re-exported package types
export type {
  ActionTypeTag,
  AddWorkflowItemLogRequest,
  WorkflowItem,
  WorkflowItemContributor,
  WorkflowItemLog,
  WorkflowItemLogWithoutTimestampAndUser,
  WorkflowItemWithLog,
} from '@blk/awt-aladdinwealthserver';
